import { clone, cloneDeep, merge } from 'lodash'
import getEnvironment from './getEnvironment'

class TealiumConfigError extends Error {
  /**
   * @param {string} message
   * @param {string} key
   * @param {any} value
   */
  constructor(message, key, value) {
    super(message)
    this.key = key
    this.value = value
  }
}

/**
 * The set of valid Tealium environments.
 */
const ENVIRONMENTS = ["prod", "notprod"]
const UTAG_SCRIPT_PATH_PREFIXES = ["mcoe", "main"]

/**
 * @param {Partial<TealiumConfig>} config
 * @returns {TealiumConfig}
 */
export function validateConfig ({
  customTrackingAttribute = '',
  untrackedSelectors = '',
  ddo,
  enableAppendAriaExpanded = false,
  enableAppendAriaExpandedInversion = false,
  enableDeviceTracking = true,
  enableScrollTracking = true,
  enableVideoControlsTracking = false,
  enableVTIMTracking = false,
  env,
  scrollPercentiles = null, // formerly scrollBreakpoints
  utagScriptPathPrefix = "mcoe",
  timeout = null,
  videoPercentiles = null,
  viewports = null,
  kalturaConfig
} = {}) {
  env = env || getEnvironment()
  if (!ENVIRONMENTS.includes(env)) {
    throw new TealiumConfigError(
      `"${env}" is not a valid Tealium environment: env must be one of: ${ENVIRONMENTS.join(', ')}`,
      'env',
      env
    )
  }

  if (!UTAG_SCRIPT_PATH_PREFIXES.includes(utagScriptPathPrefix)) {
    throw new TealiumConfigError(
      `"${utagScriptPathPrefix}" is not a valid Tealium utag.js path prefix: utagScriptPathPrefix must be one of: ${UTAG_SCRIPT_PATH_PREFIXES.join(', ')}`,
      'utagScriptPathPrefix',
      utagScriptPathPrefix
    )
  }

  if (enableVTIMTracking) {
    // If VTIM is enabled, several booleans are locked:
    enableDeviceTracking = false
    enableAppendAriaExpanded = true
    enableScrollTracking = false
  }

  return {
    customTrackingAttribute,
    untrackedSelectors,
    ddo: merge(cloneDeep(baseDDOObject), ddo, {
      pageInstanceID: env
    }),
    enableAppendAriaExpanded,
    enableAppendAriaExpandedInversion,
    enableDeviceTracking,
    enableScrollTracking,
    enableVideoControlsTracking,
    enableVTIMTracking,
    env,
    scrollPercentiles,
    utagScriptPathPrefix,
    timeout,
    videoPercentiles,
    viewports,
    kalturaConfig: merge(cloneDeep(baseKalturaConfig), kalturaConfig, {}),
  }
}

export const basePageObject = {
  pageID: "",
  destinationURL: null,
  referringURL: null,
  issueDate: null,
  language: "en-us",
  segmentValue: null,
  appName: null,
  appStepNumber: null,
  appStepName: null,
  attr: "-_--_--_--_--_--_--_--_--_--_--_--_--_--_-"
}

export const baseKalturaConfig = {
  trackPlayer: true,
  trackInteraction: false,
  trackWatchTime: true
}

// v2 events documentation: http://player.kaltura.com/docs/api#firstPlay
// v7 player events docs: https://github.com/kaltura/playkit-js/blob/master/src/event/event-type.js 
// v7 UI events docs: https://github.com/kaltura/playkit-js-ui/blob/master/docs/events.md
export const KalturaEvents = {
  FirstPlay: {
    type: 'player',
    v2key: 'firstPlay',
    v7key: 'FIRST_PLAY',
    kalturaStateEvents: [ 'KMP_FirstPlay' ]
  },
  Play: {
    type: 'player',
    v2key: 'playerPlayed',
    v7key: 'PLAY',
    kalturaStateEvents: [ 'KMP_Play' ]
  },
  Pause: {
    type: 'player',
    v2key: 'playerPaused',
    v7key: 'PAUSE',
    kalturaStateEvents: [ 'KMP_Pause' ]
  },
  Complete: {
    type: 'player',
    v2key: 'playerPlayEnd',
    v7key: 'PLAYBACK_ENDED',
    kalturaStateEvents: [ 'KMP_Complete' ]
  },
  '25pct': {
    type: 'watchTime',
    v2key: 'firstQuartile',
    v7key: 1,
    kalturaStateEvents: [ 'KMP_25pct' ]
  },
  '50pct': {
    type: 'watchTime',
    v2key: 'secondQuartile',
    v7key: 2,
    kalturaStateEvents: [ 'KMP_50pct' ]
  },
  '75pct': {
    type: 'watchTime',
    v2key: 'thirdQuartile',
    v7key: 3,
    kalturaStateEvents: [ 'KMP_75pct' ]
  },
  clickSeek: {
    type: 'interaction',
    v2key: 'seek',
    v7key: 'USER_SEEKED',
    kalturaStateEvents: [ 'KMP_UserSeeked']
  },
}

export const baseDDOObject = {
  pageInstanceID: null,
  load_coremetrics: true,
  load_opinionlabs: false,
  load_touchcommerce: false,
  load_audiencemanager: true,
  load_adobetarget: true,
  disable_pageviewonload: false,
  page: {
    pageInfo: [],
    category: {
      primaryCategory: '',
      addlCategory: null,
      pageType: null
    },
    attributes: {
      searchString: null,
      searchResults: null,
      olbSessionID: null,
      subCampaignCode: null,
      pageIDHistory: [],
      DARTUrl: null,
      stateCookie: null,
      SASIEnabled: false,
      needOLBcookie: false,
      standardDART: [],
      standardDARTes: [],
      clickDART: [],
      clickDARTes: [],
      gaId: null,
      chat: {
        account_type: null,
        boa_associate: null,
        boa_retiree: null,
        customer_lob: null,
        customer_segment: null,
        data: null,
        email_campaign: null,
        entitlement_code: null,
        error_category: null,
        error_count: null,
        first_login: null,
        inqClientOrderNum: null,
        inqOrderType: null,
        inqOtherInfo: null,
        inqSalesPrices: [],
        inqSalesProducts: [],
        inqSalesProductTypes: null,
        inqSalesQuantities: [],
        invitation_background: null,
        invitation_template: null,
        referral_campaign: null,
        getStateValue: false,
        cust_fn: null,
        cust_ln: null,
        target: null,
        timezone: null,
        page_loadtime: null
      }
    }
  },
  product: [{
    productInfo: {
      productID: null,
      productName: '',
      type: null,
      tagIsCalled: null
    },
    category: {
      primaryCategory: null
      // addlCategory[n]:null
    },
    attributes: {
      //   //Extensible attributes for product[n]
    }
  }],
  user: {
    segment: null,
    online_id: null,
    name: {
      first: null,
      last: null,
      full: null
    },
    offers: [],
    state: null,
    preferred_rewards_tier: null,
    olb3rdpartyid: null,
    authenticated: false
  },
  version: 'BAC_0.16'
}

/**
 * @typedef {object} TealiumConfig
 * @prop {string} customTrackingAttribute
 * @prop {string | string[]} untrackedSelectors
 * @prop {any} ddo
 * @prop {boolean} enableAppendAriaExpanded
 * @prop {boolean} enableAppendAriaExpandedInversion
 * @prop {boolean} enableDeviceTracking
 * @prop {boolean} enableScrollTracking
 * @prop {boolean} enableVideoControlsTracking
 * @prop {boolean} enableVTIMTracking
 * @prop {'prod' | 'notprod'} env
 * @prop {number[] | null} scrollPercentiles
 * @prop {number | null} timeout
 * @prop {number[] | null} videoPercentiles
 * @prop {import('./viewportListener').Viewport[] | null} viewports
 */
